import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box, MenuItem, Select, FormControl, InputLabel, Grid, Alert, IconButton, Divider
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];
const unitOptions = ['Pcs', 'Pair', 'Set'];

const UpdateDirectOrder = () => {
  const [karigar, setKarigar] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [salesman, setSalesman] = useState('');
  const [order, setOrder] = useState({ billNumber: '', orderDate: '', deliveryDate: '', requiredDate: '', isUrgent: false });
  const [items, setItems] = useState([
    { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }
  ]);
  const [itemOptions, setItemOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [directOrderId, setDirectOrderId] = useState(null);
  const [shouldFetchDirectOrder, setShouldFetchDirectOrder] = useState(false);
  const lastAttemptedBillNumber = useRef('');
  const [directOrderExists, setDirectOrderExists] = useState(false);
  const [orderFetched, setOrderFetched] = useState(false);

  useEffect(() => {
    const fetchKarigars = async () => {
      try {
        const res = await api.get('/api/karigar');
        setKarigars(res.data);
      } catch {}
    };
    const fetchItems = async () => {
      try {
        const res = await api.get('/api/items');
        setItemOptions(res.data.map(item => item.name));
      } catch {}
    };
    fetchKarigars();
    fetchItems();
  }, []);

  // Fetch direct order for update
  useEffect(() => {
    if (shouldFetchDirectOrder && order.billNumber) {
      api.get(`/api/directorders?billNumber=${encodeURIComponent(order.billNumber)}`)
        .then(res => {
          if (Array.isArray(res.data) && res.data.length > 0) {
            setDirectOrderExists(true);
            setOrder(res.data[0]);
            setItems(res.data[0].items || [{ itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
            setKarigar(res.data[0].karigarAbbreviation || '');
            setSalesman(res.data[0].agentName || '');
            setDirectOrderId(res.data[0]._id);
            setOrderFetched(true);
            setError('');
          } else {
            setDirectOrderExists(false);
            setOrderFetched(false);
            setError('Direct order not found.');
          }
        })
        .catch(() => { setDirectOrderExists(false); setOrderFetched(false); setError('Direct order not found.'); });
      setShouldFetchDirectOrder(false);
    }
  }, [shouldFetchDirectOrder, order.billNumber]);

  const handleKarigarChange = e => setKarigar(e.target.value);
  const handleSalesmanChange = e => setSalesman(e.target.value);
  const handleOrderChange = e => {
    const { name, value, type, checked } = e.target;
    setOrder(o => ({ ...o, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'billNumber') localStorage.setItem('sharedBillNumber', value);
  };
  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setItems(items => items.map((item, i) => i === idx ? { ...item, [name]: value } : item));
  };
  const addItem = () => setItems(items => [...items, { itemName: '', karat: '', quantity: '', unit: '', itemWeight: '' }]);
  const removeItem = idx => setItems(items => items.length > 1 ? items.filter((_, i) => i !== idx) : items);

  const validate = () => {
    if (!karigar || !karigar.trim()) return 'Karigar is required.';
    if (!salesman || !salesman.trim()) return 'Salesman is required.';
    if (!order.billNumber || !order.billNumber.trim() || !order.orderDate || !order.deliveryDate || !order.requiredDate) return 'All order fields are required.';
    for (const item of items) {
      if (!item.itemName || !item.itemName.trim() || !item.karat || !item.karat.trim() || !item.quantity || !item.unit || !item.unit.trim() || !item.itemWeight) return 'All item fields are required.';
    }
    return '';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const err = validate();
    if (err) { setError(err); return; }
    if (!directOrderExists || !directOrderId) {
      setError('No direct order loaded to update.');
      return;
    }
    setLoading(true);
    try {
      // Calculate totalAmount for each item and for the order
      const computedItems = items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const rate = parseFloat(item.rate) || 0;
        const makingCharge = parseFloat(item.makingCharge) || 0;
        const stoneAmount = parseFloat(item.stoneAmount) || 0;
        const total = weight + wastage;
        const netAmount = weight ? ((weight / 11.664) * rate) : 0;
        const totalAmount = netAmount + makingCharge + stoneAmount;
        return {
          ...item,
          total: total ? total.toFixed(3) : '',
          netAmount: netAmount ? netAmount.toFixed(2) : '',
          totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
        };
      });
      const orderTotalAmount = computedItems.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
      await api.put(`/api/directorders/${directOrderId}`, {
        billNumber: order.billNumber,
        orderDate: order.orderDate,
        deliveryDate: order.deliveryDate,
        requiredDate: order.requiredDate,
        status: 'Pending',
        agentName: salesman,
        karigarAbbreviation: karigar,
        items: computedItems.map(item => ({
          ...item,
          quantity: Number(item.quantity),
          itemWeight: Number(item.itemWeight),
          total: Number(item.total),
          netAmount: Number(item.netAmount),
          totalAmount: Number(item.totalAmount),
          gsb: Number(item.gsb) || 0,
          wastage: Number(item.wastage) || 0,
          rate: Number(item.rate) || 0,
          makingCharge: Number(item.makingCharge) || 0,
          stoneAmount: Number(item.stoneAmount) || 0,
          despatchStatus: true,
          workIssued: false,
          karigarAbbreviation: karigar
        })),
        totalAmount: orderTotalAmount,
        isUrgent: false,
        isActive: true
      });
      setSuccess('Direct order updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update direct order');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Update Direct Order
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {!orderFetched && (
          <Box component="form" sx={{ mb: 4 }} onSubmit={e => { e.preventDefault(); setShouldFetchDirectOrder(true); }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="Bill Number" name="billNumber" value={order.billNumber} onChange={handleOrderChange} required fullWidth autoComplete="off" size="small" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button type="submit" variant="contained" color="primary">Fetch Direct Order</Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {orderFetched && (
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mt: 2 }}>Order Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Karigar Name</InputLabel>
                  <Select value={karigar} label="Karigar Name" onChange={handleKarigarChange}>
                    {karigars.map(k => (
                      <MenuItem key={k.abbreviation} value={k.abbreviation}>
                        {k.abbreviation} - {k.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Salesman" name="salesman" value={salesman} onChange={handleSalesmanChange} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Bill Number" name="billNumber" value={order.billNumber} onChange={handleOrderChange} onBlur={e => { lastAttemptedBillNumber.current = e.target.value; setShouldFetchDirectOrder(true); }} required fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Order Date" name="orderDate" type="date" value={order.orderDate} onChange={handleOrderChange} required fullWidth InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Delivery Date" name="deliveryDate" type="date" value={order.deliveryDate} onChange={handleOrderChange} required fullWidth InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Required Date" name="requiredDate" type="date" value={order.requiredDate} onChange={handleOrderChange} required fullWidth InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>Order Items</Typography>
            {items.map((item, idx) => (
              <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Item Name</InputLabel>
                    <Select name="itemName" value={item.itemName} label="Item Name" onChange={e => handleItemChange(idx, e)}>
                      {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Karat</InputLabel>
                    <Select name="karat" value={item.karat} label="Karat" onChange={e => handleItemChange(idx, e)}>
                      {karatOptions.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField label="Quantity" name="quantity" type="number" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Unit</InputLabel>
                    <Select name="unit" value={item.unit} label="Unit" onChange={e => handleItemChange(idx, e)}>
                      {unitOptions.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField label="Item Weight" name="itemWeight" type="number" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth size="small" />
                </Grid>
              </Grid>
            ))}
            <Box sx={{ mt: 2 }}>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Direct Order'}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default UpdateDirectOrder; 